import { Mentor } from '../../../mocks/mentors';
import { sanitizeRichHtml } from '@/lib/richText';
import Avatar from '@/components/base/Avatar';

interface Props {
  mentor: Mentor;
  onBook: () => void;
}

const ProfileCard = ({ mentor, onBook }: Props) => {
  const richBio = sanitizeRichHtml(mentor.bio);

  return (
    <section className="bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-10 items-start">
          {/* Avatar + Stats */}
          <div className="flex-shrink-0 flex flex-col items-center w-52">
            <div className="relative">
              <Avatar src={mentor.avatar} alt={mentor.name} className="w-44 h-56 rounded-2xl" />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-green-50 border border-green-200 rounded-full px-3 py-1 flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-700 text-xs font-medium">接受預約中</span>
              </div>
            </div>

            <div className="mt-8 w-full bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
              <div className="text-2xl font-bold text-[#0A1628]">{mentor.rating}</div>
              <div className="flex justify-center gap-0.5 my-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-amber-400 text-xs" />
                ))}
              </div>
              <div className="text-slate-400 text-xs">{mentor.reviews} 則評價</div>
            </div>

            <div className="mt-3 w-full text-center">
              <div className="text-2xl font-bold text-[#0A1628]">{mentor.sessions}+</div>
              <div className="text-slate-400 text-xs mt-0.5">完成諮詢次數</div>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-[#0A1628]">{mentor.name}</h1>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold border"
                    style={{ color: mentor.companyColor, borderColor: mentor.companyColor + '40', backgroundColor: mentor.companyColor + '12' }}
                  >
                    {mentor.company}
                  </span>
                </div>
                <p className="text-slate-500 text-base font-medium">{mentor.title}</p>
              </div>

              {/* 单一 CTA */}
              <button
                onClick={onBook}
                className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap hover:bg-violet-700 active:scale-[0.98] transition-all flex-shrink-0"
              >
                <i className="ri-calendar-check-line" />
                立即預約
                <span className="bg-white/20 text-white/90 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                  ${mentor.price} / 次
                </span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {mentor.skills.map((skill) => (
                <span key={skill} className="px-3 py-1.5 bg-violet-50 text-violet-700 text-xs font-semibold rounded-full border border-violet-100">
                  {skill}
                </span>
              ))}
            </div>

            <div className="mentor-rich-content prose-custom mb-6 max-w-3xl text-slate-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: richBio }} />

            <div className="grid grid-cols-3 gap-4 mb-5">
              {[
                { icon: 'ri-translate-2', label: '語言', value: mentor.languages.join(' · ') },
                { icon: 'ri-global-line', label: '時區', value: mentor.timezone },
                { icon: 'ri-shield-check-line', label: '認證狀態', value: '已通過 NexusGo 認證', color: 'text-green-600' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
                    <i className={`${item.icon} text-slate-500 text-sm`} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">{item.label}</div>
                    <div className={`text-sm font-medium ${item.color ?? 'text-[#0A1628]'}`}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">專業領域</div>
              <div className="flex flex-wrap gap-2">
                {mentor.expertise.map((exp) => (
                  <span key={exp} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-lg">{exp}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileCard;
